import React, { useState, useEffect } from 'react';
import './OrdersFilters.css';

interface FloorOption {
    value: string | null;
    label: string;
}

interface OrdersFiltersProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (filters: FilterPayload) => void;
    floorOptions: FloorOption[];
    currentFilters: {
        floorId: string | null;
        status: number | null;
        customerName: string;
        orderNumber: string;
        minAmount: string;
        maxAmount: string;
    };
}

export interface FilterPayload {
    floorId: string | null;
    status: number | null;
    customerName: string | null;
    orderNumber: string | null;
    minAmount: number | null;
    maxAmount: number | null;
}

const OrdersFilters: React.FC<OrdersFiltersProps> = ({
    isOpen,
    onClose,
    onSave,
    floorOptions,
    currentFilters
}) => {
    const statusOptions = [
        { value: null, label: 'All Statuses' },
        { value: 0, label: 'Submit' },
        { value: 1, label: 'Progress' },
        { value: 2, label: 'Served' },
        { value: 3, label: 'Done' }
    ];

    const [localFloorId, setLocalFloorId] = useState<string | null>(currentFilters.floorId);
    const [localStatus, setLocalStatus] = useState<number | null>(currentFilters.status);
    const [localCustomerName, setLocalCustomerName] = useState(currentFilters.customerName);
    const [localOrderNumber, setLocalOrderNumber] = useState(currentFilters.orderNumber);
    const [localMinAmount, setLocalMinAmount] = useState(currentFilters.minAmount);
    const [localMaxAmount, setLocalMaxAmount] = useState(currentFilters.maxAmount);

    useEffect(() => {
        if (isOpen) {
            setLocalFloorId(currentFilters.floorId);
            setLocalStatus(currentFilters.status);
            setLocalCustomerName(currentFilters.customerName);
            setLocalOrderNumber(currentFilters.orderNumber);
            setLocalMinAmount(currentFilters.minAmount);
            setLocalMaxAmount(currentFilters.maxAmount);
        }
    }, [isOpen, currentFilters]);

    const handleSave = () => {
        const payload: FilterPayload = {
            floorId: localFloorId || null,
            status: localStatus,
            customerName: localCustomerName || null,
            orderNumber: localOrderNumber || null,
            minAmount: localMinAmount ? parseFloat(localMinAmount) : null,
            maxAmount: localMaxAmount ? parseFloat(localMaxAmount) : null
        };
        onSave(payload);
        onClose();
    };

    const handleReset = () => {
        setLocalFloorId(null);
        setLocalStatus(null);
        setLocalCustomerName('');
        setLocalOrderNumber('');
        setLocalMinAmount('');
        setLocalMaxAmount('');
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setLocalStatus(value === '' ? null : parseInt(value));
    };

    return (
        <div className={`orders-filters ${isOpen ? 'openSidePanel' : ''}`}>
            <div className='d-flex align-items-center w-100 justify-content-between header pb-3'>
                <h5 style={{ marginBottom: 0, fontWeight: 600 }}>Filters</h5>
                <button className='text-btn' onClick={onClose}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 1L1 13M1 1L13 13" stroke="#212529" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
            <hr className='mt-0' style={{ margin: 0 }} />

            <div style={{ paddingInline: '20px', flex: '1', overflowY: 'auto' }}>
                <div className='d-flex flex-column mt-3 w-100'>
                    <label>Floor</label>
                    <select
                        value={localFloorId || ''}
                        onChange={(e) => setLocalFloorId(e.target.value || null)}
                    >
                        <option value="">All Floors</option>
                        {floorOptions.map((floor) => (
                            <option key={floor.value} value={floor.value || ''}>
                                {floor.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className='d-flex flex-column mt-3 w-100'>
                    <label>Order Number</label>
                    <input
                        type='text'
                        value={localOrderNumber}
                        onChange={(e) => setLocalOrderNumber(e.target.value)}
                        placeholder="Enter order number"
                    />
                </div>

                <div className='d-flex flex-column mt-3 w-100'>
                    <label>Customer Name</label>
                    <input
                        type='text'
                        value={localCustomerName}
                        onChange={(e) => setLocalCustomerName(e.target.value)}
                        placeholder="Enter customer name"
                    />
                </div>

                <div className='d-flex flex-column mt-3 w-100'>
                    <label>Status</label>
                    <select
                        value={localStatus === null ? '' : localStatus}
                        onChange={handleStatusChange}
                    >
                        {statusOptions.map((option) => (
                            <option key={option.value === null ? 'all' : option.value} value={option.value === null ? '' : option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className='d-flex flex-column mt-3 w-100'>
                    <label>Order Amount</label>
                    <div className='d-flex gap-2 align-items-center'>
                        <input
                            type='number'
                            value={localMinAmount}
                            onChange={(e) => setLocalMinAmount(e.target.value)}
                            placeholder="Min"
                            style={{ width: '45%' }}
                        />
                        <span>-</span>
                        <input
                            type='number'
                            value={localMaxAmount}
                            onChange={(e) => setLocalMaxAmount(e.target.value)}
                            placeholder="Max"
                            style={{ width: '45%' }}
                        />
                    </div>
                </div>
            </div>

            <div className='d-flex mt-4 p-3 align-items-center justify-content-end w-100' style={{ gap: '10px', borderTop: '1px solid #D0D5DD', position: 'relative', bottom: 0, backgroundColor: "#fff" }}>
                <button className='outline-btn' onClick={handleReset}>
                    Reset all
                </button>
                <button className='blue-filled-btn align-items-center d-flex' onClick={handleSave}>
                    Apply Filters
                </button>
            </div>
        </div>
    );
};

export default OrdersFilters;
